import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Divider,
  Group,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useWarehouses } from '../warehouses/useWarehouses';
import { listPaymentTerms, paymentTermLabel } from '../paymentTerms/api';
import {
  createSupplier,
  deleteSupplier,
  listSupplierWarehouses,
  setSupplierWarehouses,
  updateSupplier,
  type SupplierDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to create. */
  supplier: SupplierDto | null;
  onSaved: () => void;
}

interface FormState {
  supplierNumber: string;
  supplierName: string;
  contactName: string;
  email: string;
  phone: string;
  contactFax: string;
  addressLine1: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  paymentTermId: string;
  comments: string;
  isActive: boolean;
  warehouseIds: string[];
}

const EMPTY: FormState = {
  supplierNumber: '',
  supplierName: '',
  contactName: '',
  email: '',
  phone: '',
  contactFax: '',
  addressLine1: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  paymentTermId: '',
  comments: '',
  isActive: true,
  warehouseIds: [],
};

/**
 * Creates or edits one supplier, carrying the same fields the current product's screen does — plus
 * its domain grid, as a warehouse multi-select.
 *
 * The supplier number is set once and read-only afterwards: it identifies the supplier to everything
 * outside the database, so the API will not accept a change either.
 *
 * Credit limit and the external id are deliberately absent. Both are columns the current product
 * carries and does not show; they exist for the migration and the import API.
 */
export function SupplierFormModal({ opened, onClose, supplier, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = supplier !== null;

  const warehouses = useWarehouses();

  const terms = useQuery({
    queryKey: ['payment-terms', 'active'] as const,
    queryFn: ({ signal }) => listPaymentTerms(false, signal),
    enabled: opened,
  });

  // Fetched rather than derived from warehouseCount: the list only knows how many, and the form
  // needs which.
  const assigned = useQuery({
    queryKey: ['supplier-warehouses', supplier?.supplierId] as const,
    queryFn: ({ signal }) => listSupplierWarehouses(supplier!.supplierId, signal),
    enabled: opened && isEdit,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm({
      supplierNumber: supplier?.supplierNumber ?? '',
      supplierName: supplier?.supplierName ?? '',
      contactName: supplier?.contactName ?? '',
      email: supplier?.email ?? '',
      phone: supplier?.phone ?? '',
      contactFax: supplier?.contactFax ?? '',
      addressLine1: supplier?.addressLine1 ?? '',
      city: supplier?.city ?? '',
      stateProvince: supplier?.stateProvince ?? '',
      postalCode: supplier?.postalCode ?? '',
      paymentTermId: supplier?.paymentTermId ?? '',
      comments: supplier?.comments ?? '',
      isActive: supplier?.isActive ?? true,
      warehouseIds: [],
    });
  }, [opened, supplier]);

  // Arrives after the form is seeded, so it is applied separately rather than racing the reset.
  useEffect(() => {
    if (!opened || !assigned.data) return;
    setForm((current) => ({ ...current, warehouseIds: assigned.data.map((w) => w.warehouseId) }));
  }, [opened, assigned.data]);

  const warehouseOptions = useMemo(
    () =>
      (warehouses.data ?? [])
        // An inactive warehouse stays selectable when the supplier is already assigned to it:
        // hiding it would silently unassign on the next save.
        .filter((w) => w.isActive || form.warehouseIds.includes(w.warehouseId))
        .map((w) => ({ value: w.warehouseId, label: `${w.warehouseCode} — ${w.displayName}` })),
    [warehouses.data, form.warehouseIds],
  );

  const termOptions = useMemo(
    () => (terms.data ?? []).map((term) => ({ value: term.paymentTermId, label: paymentTermLabel(term) })),
    [terms.data],
  );

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        supplierName: form.supplierName.trim(),
        contactName: blankToNull(form.contactName),
        email: blankToNull(form.email),
        phone: blankToNull(form.phone),
        contactFax: blankToNull(form.contactFax),
        addressLine1: blankToNull(form.addressLine1),
        city: blankToNull(form.city),
        stateProvince: blankToNull(form.stateProvince),
        postalCode: blankToNull(form.postalCode),
        paymentTermId: form.paymentTermId || null,
        comments: blankToNull(form.comments),
        isActive: form.isActive,
      };

      const saved = isEdit
        ? await updateSupplier(supplier!.supplierId, body)
        : await createSupplier({ ...body, supplierNumber: form.supplierNumber.trim() });

      // Two calls because they are two resources. The supplier is saved either way; if the
      // assignment fails the error names that, rather than implying nothing was written.
      await setSupplierWarehouses(saved.supplierId, form.warehouseIds);
      return saved;
    },
    onSuccess: () => {
      notifications.show({ message: t('suppliers.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteSupplier(supplier!.supplierId),
    onSuccess: () => {
      notifications.show({ message: t('suppliers.deleted'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.problem.type?.includes('duplicate_supplier_number')) {
        return t('suppliers.errors.duplicateNumber', { number: form.supplierNumber.trim() });
      }
      return err.problem.detail ?? err.problem.title ?? t('suppliers.errors.saveFailed');
    }
    return t('suppliers.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: typeof errors = {};
    if (!isEdit && !form.supplierNumber.trim()) next.supplierNumber = t('suppliers.errors.numberRequired');
    if (!form.supplierName.trim()) next.supplierName = t('suppliers.errors.nameRequired');

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('suppliers.editTitle', { name: supplier!.supplierName }) : t('suppliers.newTitle')}
      width="lg"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('suppliers.number')}
              description={isEdit ? t('suppliers.numberFixed') : t('suppliers.numberHint')}
              value={form.supplierNumber}
              onChange={(e) => setForm({ ...form, supplierNumber: e.currentTarget.value })}
              error={errors.supplierNumber}
              maxLength={50}
              disabled={isEdit}
              required
              autoFocus={!isEdit}
            />
            <TextInput
              label={t('suppliers.name')}
              value={form.supplierName}
              onChange={(e) => setForm({ ...form, supplierName: e.currentTarget.value })}
              error={errors.supplierName}
              maxLength={200}
              required
              autoFocus={isEdit}
            />
          </SimpleGrid>

          <Divider label={t('suppliers.contactSection')} labelPosition="left" />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('suppliers.contact')}
              description={t('suppliers.contactHint')}
              value={form.contactName}
              onChange={(e) => setForm({ ...form, contactName: e.currentTarget.value })}
              maxLength={255}
            />
            <TextInput
              label={t('suppliers.email')}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
              maxLength={320}
            />
            <TextInput
              label={t('suppliers.phone')}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
              maxLength={50}
            />
            <TextInput
              label={t('suppliers.fax')}
              value={form.contactFax}
              onChange={(e) => setForm({ ...form, contactFax: e.currentTarget.value })}
              maxLength={50}
            />
          </SimpleGrid>

          <Divider label={t('suppliers.addressSection')} labelPosition="left" />

          <TextInput
            label={t('suppliers.address')}
            value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.currentTarget.value })}
            maxLength={255}
          />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <TextInput
              label={t('suppliers.city')}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.currentTarget.value })}
              maxLength={100}
            />
            <TextInput
              label={t('suppliers.stateProvince')}
              value={form.stateProvince}
              onChange={(e) => setForm({ ...form, stateProvince: e.currentTarget.value })}
              maxLength={10}
            />
            <TextInput
              label={t('suppliers.postalCode')}
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.currentTarget.value })}
              maxLength={20}
            />
          </SimpleGrid>

          <Divider label={t('suppliers.tradingSection')} labelPosition="left" />

          <Select
            label={t('suppliers.terms')}
            description={
              termOptions.length === 0 ? t('suppliers.noTermsYet') : t('suppliers.termsHint')
            }
            placeholder={t('suppliers.termsPlaceholder')}
            data={termOptions}
            value={form.paymentTermId || null}
            onChange={(v) => setForm({ ...form, paymentTermId: v ?? '' })}
            disabled={terms.isLoading || termOptions.length === 0}
            clearable
            searchable
          />

          <MultiSelect
            label={t('suppliers.warehouses')}
            description={t('suppliers.warehousesHint')}
            data={warehouseOptions}
            value={form.warehouseIds}
            onChange={(v) => setForm({ ...form, warehouseIds: v })}
            disabled={warehouses.isLoading || (isEdit && assigned.isLoading)}
            searchable
            clearable
          />

          <Textarea
            label={t('suppliers.comments')}
            value={form.comments}
            onChange={(e) => setForm({ ...form, comments: e.currentTarget.value })}
            autosize
            minRows={2}
            maxRows={6}
          />

          <Switch
            label={t('suppliers.active')}
            description={t('suppliers.activeHint')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between">
            {isEdit ? (
              <Button variant="light" color="red" onClick={() => remove.mutate()} loading={remove.isPending}>
                {t('suppliers.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Group>

          {isEdit && (
            <Text size="xs" c="dimmed">
              {t('suppliers.deleteHint')}
            </Text>
          )}
        </Stack>
      </form>
    </AppModal>
  );
}

/** An all-whitespace field becomes null rather than a blank that sorts oddly and reads as data. */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
